export type TooltipPlacementArgs = {
  /** カーソルの viewport 座標 (= `position: fixed` と同じ原点)。 */
  cursor: { x: number; y: number }
  /**
   * ツールチップの実寸。0 を渡しても「反転しない」保証にはならない
   * (カーソル + offset 自体がウィンドウ外なら反転する) ので、未測定の間は
   * この関数を呼ばず表示を保留する側で扱う。
   */
  tooltip: { width: number; height: number }
  /** 見切れ判定の基準となるウィンドウ寸法。 */
  viewport: { width: number; height: number }
  /** カーソルとツールチップの間隔 (px)。 */
  offset: number
}

function clampToViewport(
  value: number,
  tooltipSize: number,
  viewportSize: number,
): number {
  return Math.min(Math.max(value, 0), Math.max(viewportSize - tooltipSize, 0))
}

/**
 * ホバー中のグラフを覆わないツールチップ配置を求める (`position: fixed` の
 * left / top)。既定はカーソルの右下で、ウィンドウから溢れる軸だけを反転する
 * (右→左下 / 下→右上 / 両方→左上)。
 *
 * why not 反転を軸で連動させる: 右端の下段カードは横だけ反転すれば収まる。
 * 連動させると必要のない縦反転が起きてカーソルの上に被り、「ホバー中もグラフ
 * 全体が見える」という配置目的を自分で壊す。
 *
 * why not はみ出した分だけスライドさせる (clamp のみ): カーソル位置へ寄って
 * いくため、右端では必ずツールチップがカーソルの下に潜り込む。clamp は反転
 * しても収まらない縮退ケース (ツールチップがウィンドウより大きい) の最後の
 * 歯止めとしてだけ使う。
 */
export function computeTooltipPlacement(args: TooltipPlacementArgs): {
  left: number
  top: number
} {
  const preferredLeft = args.cursor.x + args.offset
  const preferredTop = args.cursor.y + args.offset
  const overflowsRight =
    preferredLeft + args.tooltip.width > args.viewport.width
  const overflowsBottom =
    preferredTop + args.tooltip.height > args.viewport.height

  const left = overflowsRight
    ? args.cursor.x - args.offset - args.tooltip.width
    : preferredLeft
  const top = overflowsBottom
    ? args.cursor.y - args.offset - args.tooltip.height
    : preferredTop

  return {
    left: clampToViewport(left, args.tooltip.width, args.viewport.width),
    top: clampToViewport(top, args.tooltip.height, args.viewport.height),
  }
}
